import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Box,
  TextField,
  Button,
  IconButton,
  DialogContent,
  Dialog,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import jsQR from 'jsqr';
import { useSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';

const RegisterCampaingSource = ({ isOpen, onClose, handleSaveSources }) => {
  const [isQrScannerOpen, setIsQrScannerOpen] = useState(false);
  const [qrResult, setQrResult] = useState('');
  const [stream, setStream] = useState(null);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isFlashEnabled, setIsFlashEnabled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Estado para mostrar el spinner
  const [errorMessage, setErrorMessage] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [tempQrResult, setTempQrResult] = useState(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      setErrorMessage('No se pudo acceder a la cámara.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const toggleFlash = async () => {
    if (!stream) {
      setErrorMessage('La cámara no está activa. Por favor, enciéndela primero.');
      return;
    }

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities ? track.getCapabilities() : {};

    if ('torch' in capabilities) {
      try {
        await track.applyConstraints({
          advanced: [{ torch: !isFlashEnabled }],
        });
        setIsFlashEnabled(!isFlashEnabled);
      } catch (error) {
        setErrorMessage('No se pudo activar el flash. Verifica los permisos o usa iluminación manual.');
        console.error('Error al intentar activar el flash:', error);
      }
    } else {
      setErrorMessage(
        'El flash no es compatible con este dispositivo o navegador. Intenta iluminar manualmente el entorno.'
      );
    }
  };

  const handleCloseScanner = () => {
    setIsQrScannerOpen(false);
    stopCamera();
  };

  const scanQRCode = () => {
    if (canvasRef.current && videoRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');

      if (video.videoWidth > 0 && video.videoHeight > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          if (/^\d+$/.test(code.data)) {
            setTempQrResult(code.data);
            handleCloseScanner();
            handleSave(code.data);
          } else {
            setErrorMessage('QR incorrecto: solo se permiten números.');
          }
        }
      }
    }
  };

  const handleSave = async (data = null) => {
    setIsProcessing(true); 
    const valueToSave =
          qrResult && qrResult.trim() !== '' && qrResult !== undefined
            ? qrResult
            : tempQrResult && tempQrResult.trim() !== '' && tempQrResult !== undefined
            ? tempQrResult
            : data || null;
    try {
      await handleSaveSources(valueToSave); // Usa el valor correcto
      //onClose();
      setQrResult('');
      setTempQrResult('');
    } catch (error) {
      enqueueSnackbar('Error al guardar el documento.', { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (isQrScannerOpen) {
      startCamera();
      scanIntervalRef.current = setInterval(scanQRCode, 300);
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [isQrScannerOpen]);

  useEffect(() => {
    setIsSaveEnabled(qrResult.trim() !== '' && /^[0-9]+$/.test(qrResult));
  }, [qrResult]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setQrResult(value);
      setTempQrResult(value);
    }
  };

  const handleCloseError = () => {
    setErrorMessage('');
  };

  return (
    <div>
      <Dialog maxWidth="xs" fullWidth open={isOpen} onClose={onClose}>
        <DialogContent
          sx={{
            minHeight: '350px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            padding: 3,
          }}
        >
          <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
            Registro de Documento
          </Typography>
          <TextField
            fullWidth
            label="Número de Documento"
            variant="outlined"
            value={qrResult}
            onChange={handleInputChange}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Tooltip title="Abrir escáner QR" arrow>
              <IconButton
                color="primary"
                onClick={() =>{
                setQrResult(''); // Resetea el valor del input
                setTempQrResult(''); // También resetea el valor temporal
                setIsQrScannerOpen(true)
                }}
                sx={{
                  width: 70,
                  height: 70,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                }}
              >
               <CameraAltIcon style={{ fontSize: '40px', width: '40px', height: '40px', color: 'white' }} /> 
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mt: 2,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isSaveEnabled || isProcessing}
              startIcon={isProcessing && <CircularProgress size={20} />}
            >
              {isProcessing ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={onClose}
              sx={{ ml: 2 }}
            >
              Cerrar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>


      <Dialog open={isQrScannerOpen} onClose={handleCloseScanner} fullWidth>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {isQrScannerOpen && (
            <Box sx={{ width: '100%', height: '100%' }}>
              <video ref={videoRef} autoPlay style={{ width: '100%', height: '100%' }} />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant="contained"
                  color={isFlashEnabled ? 'secondary' : 'primary'}
                  onClick={toggleFlash}
                >
                  {isFlashEnabled ? 'Apagar Flash' : 'Encender Flash'}
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleCloseScanner}>
                  Cerrar
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="warning" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RegisterCampaingSource;
